import type { Transaction } from '@cambio/shared';
import { createFileRoute } from '@tanstack/react-router';
import { Receipt, Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CurrencySwitcher } from '@/components/CurrencySwitcher';
import { DecisionCard } from '@/components/DecisionCard';
import { AppShell } from '@/components/layout/AppShell';
import { TransactionList } from '@/components/TransactionList';
import { useTransactionComposer } from '@/components/TransactionComposer';
import { TransactionSheet } from '@/components/TransactionSheet';
import { Button, EmptyState, Skeleton } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { ApiRequestError } from '@/lib/api';
import { cx } from '@/lib/format';
import { useDeleteTransaction, useMe, useRestoreTransaction, useTransactions } from '@/lib/queries';
import { useSelectedCurrency } from '@/lib/useSelectedCurrency';

export const Route = createFileRoute('/app/ledger')({ component: Ledger });

type SideFilter = 'all' | 'buy' | 'sell';

function Ledger() {
  const { t } = useTranslation();
  const toast = useToast();
  const { openComposer } = useTransactionComposer();
  const { data: me } = useMe();

  const {
    quote,
    select,
    available,
    positions,
    isLoading: positionsLoading,
  } = useSelectedCurrency();

  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [side, setSide] = useState<SideFilter>('all');
  const [editing, setEditing] = useState<Transaction | null>(null);

  const { data, isLoading } = useTransactions({
    ...(quote ? { quote } : {}),
    ...(search ? { q: search } : {}),
    ...(side === 'all' ? {} : { side }),
    limit: 200,
  });

  const remove = useDeleteTransaction();
  const restore = useRestoreTransaction();

  const position = useMemo(
    () => positions.find((p) => p.quoteCurrency === quote),
    [positions, quote],
  );

  const onDelete = async (tx: Transaction) => {
    try {
      await remove.mutateAsync(tx.id);
      // Soft delete server-side, so undo restores the original row rather than
      // creating a lookalike with a new id.
      toast.show(t('ledger.deleted'), {
        action: {
          label: t('common.undo'),
          onClick: () => void restore.mutateAsync(tx.id),
        },
      });
    } catch (error) {
      toast.show(
        error instanceof ApiRequestError && error.isReadOnly
          ? t('errors.readOnly')
          : t('errors.generic'),
        { tone: 'error' },
      );
    }
  };

  const transactions = data?.items ?? [];
  const showEmpty = !isLoading && transactions.length === 0;

  return (
    <AppShell
      title={t('ledger.title')}
      onSearch={() => setSearchOpen((v) => !v)}
      toolbar={<CurrencySwitcher available={available} value={quote} onChange={select} />}
      {...(quote ? { addDefaults: { quoteCurrency: quote } } : {})}
    >
      <div className="flex flex-col gap-5">
        <div className="lg:hidden">
          <CurrencySwitcher available={available} value={quote} onChange={select} />
        </div>

        {searchOpen && (
          <div className="fade-in grid gap-3 rounded-[12px] border border-separator bg-[var(--bg-elevated)] p-3 shadow-sm md:grid-cols-[minmax(0,1fr)_auto_auto] md:items-center">
            <label className="flex h-10 min-w-0 items-center gap-2 rounded-[8px] border border-separator bg-[var(--fill-4)] px-3 focus-within:border-[var(--accent)]/50 focus-within:ring-2 focus-within:ring-[var(--accent)]/10">
              <Search size={16} className="shrink-0 text-secondary" aria-hidden />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('ledger.searchPlaceholder')}
                aria-label={t('common.search')}
                className="min-w-0 flex-1 bg-transparent text-[14px] outline-none placeholder:text-tertiary"
              />
            </label>

            <div
              role="radiogroup"
              aria-label={t('ledger.filterLabel')}
              className="grid grid-cols-3 rounded-[8px] border border-separator bg-[var(--fill-4)] p-1"
            >
              {(['all', 'buy', 'sell'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={side === value}
                  onClick={() => setSide(value)}
                  className={cx(
                    'h-8 rounded-[6px] px-3 text-[12px] font-semibold transition-colors',
                    side === value
                      ? 'bg-[var(--bg-elevated)] text-primary shadow-sm'
                      : 'text-secondary hover:text-primary',
                  )}
                >
                  {value === 'all'
                    ? t('common.all')
                    : value === 'buy'
                      ? t('ledger.filterBuy')
                      : t('ledger.filterSell')}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => {
                setSearch('');
                setSide('all');
                setSearchOpen(false);
              }}
              aria-label={t('common.close')}
              className="press flex size-9 items-center justify-center justify-self-end rounded-[8px] border border-separator text-secondary hover:bg-[var(--fill-4)] hover:text-primary"
            >
              <X size={15} strokeWidth={2.3} />
            </button>
          </div>
        )}

        {positionsLoading ? (
          <Skeleton className="h-72 w-full" />
        ) : position ? (
          <DecisionCard position={position} />
        ) : null}

        <div className="flex items-end justify-between border-b border-separator pb-3 pt-2">
          <div>
            <h2 className="text-[17px] font-semibold tracking-[-0.02em]">{t('ledger.history')}</h2>
            <p className="mt-1 text-[11px] text-secondary">
              {t('ledger.historyHint', { count: transactions.length })}
            </p>
          </div>
          {(search || side !== 'all') && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                setSide('all');
              }}
              className="text-[12px] font-semibold text-[var(--accent)]"
            >
              {t('common.close')}
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="flex flex-col gap-2 pt-4">
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-56 w-full" />
          </div>
        ) : showEmpty ? (
          <EmptyState
            icon={<Receipt size={40} strokeWidth={1.5} />}
            title={search || side !== 'all' ? t('ledger.noResults') : t('ledger.empty')}
            body={search || side !== 'all' ? undefined : t('ledger.emptyHint')}
            action={
              search || side !== 'all' ? undefined : (
                <Button
                  variant="primary"
                  onClick={() => openComposer(quote ? { quoteCurrency: quote } : undefined)}
                >
                  {t('ledger.addFirst')}
                </Button>
              )
            }
          />
        ) : (
          <TransactionList
            transactions={transactions}
            onEdit={setEditing}
            onDelete={(tx) => void onDelete(tx)}
            readOnly={me?.isDemo ?? false}
          />
        )}
      </div>

      <TransactionSheet
        open={editing !== null}
        onClose={() => setEditing(null)}
        editing={editing}
      />
    </AppShell>
  );
}
